import { Alert, ArrowLeftOutlined, Button, Modal, ReloadOutlined } from 'gokwik-ui-kit'
import { NON_OCR_DOCS, UPLOAD_DOCS_GUIDELINES } from '../constants'
import DocsUploadCard from '../components/docs-upload/docsUploadCard'
import { useEffect, useState } from 'react'
import { makeAPICall } from '@gokwik/utilities'
import APIEndPoints from '@library/utilities/constants/apiEndpoints'
import { getKycDoc } from '@pages/onboarding/kyc/utils'
import GstIcon from '@library/images/icons/kyc/gst-icon.svg'
import { fetchUserDetails } from '@store/user'
import { useAppDispatch } from '@library/utilities/hooks'
import { getMerchantDetails, getUserData } from '@store/user/selectors'
import { useSelector } from 'react-redux'
import GenericAlert from '../components/customAlert'
import DocsStatusModal from '../components/docs-upload/docsStatusModal'
import SampleDocumentPreview from '../components/docs-upload/sampleDocumentPreview'
import DocsUploadModal from '../components/docs-upload/docsUploadModal'
import { DocumentPreviewModal } from '../components/docs-upload/docsPreviewModal'
import { getDocIconByName } from '../utils'
import { newStepConfiguration, stepConfiguration } from '@pages/onboarding/stepsConfig'
import { logEvent } from '@library/utilities/userLogsEvents/userLogEvents'

const DocumentsUploads = ({ setCurrentStep, currentStep, hasGenericError }) => {
    const userData = useSelector(getUserData)
    const dispatch = useAppDispatch()
    const [documentList, setDocumentList] = useState([])
    const [currentDoc, setCurrentDoc] = useState(null)
    const merchant_details = useSelector(getMerchantDetails)
    const [showPreview, setShowPreview] = useState(false)
    const [previewDoc, setPreviewDoc] = useState({})
    const [openUploadModal, setOpenUploadModal] = useState(false)
    const [visible, setVisible] = useState(false)
    const [documentUrl, setDocumentUrl] = useState('')
    const [loadingAI, setLoadingAI] = useState(false)
    const [inputImageName, setInputImageName] = useState(null)
    const [openStatusModal, setOpenStatusModal] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [isUploading, setIsUploading] = useState(false)
    const [uploadedFileName, setUploadedFileName] = useState('')
    const [ocrResult, setOcrResult] = useState([])
    const [resolvedErrors, setResolvedErrors] = useState<string[]>([])
    const [showLowScoreAlert, setShowLowScoreAlert] = useState(false)
    const [ocrApiError, setOcrApiError] = useState(false)
    const [uploadedFile, setUploadedFile] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const onUploadClick = (doc) => {
        setCurrentDoc(doc)
        setOpenUploadModal(true)
    }

    const getDocumentList = async () => {
        let res = await makeAPICall({
            method: 'get',
            url: process.env.REACT_APP_BASE_URL + APIEndPoints.documentList,
            skipLoader: true,
        })

        groupBy(res.data.data)
    }

    function groupBy(dataArray) {
        const groupedData = []

        dataArray?.forEach((item) => {
            if (
                'uploaded' in item

                // userData?.merchantDetails?.onboarding_status?.kwik_checkout_app === 'kyc_verification_failed' &&
            ) {
                if (!item?.uploaded) {
                    sessionStorage.setItem('all_docs_uploaded', 'false')
                }
            }

            const obj = {}
            const title = item.title || 'Address Proof' // 'Others' is used for items without a title

            obj['title'] = title
            obj['label'] = item?.label
            obj['desc'] = item?.tooltip || ''
            obj['logo'] = getDocIconByName(item.name)
            obj['details'] = item
            obj['format_requirements'] = item?.format_requirements

            groupedData?.push(obj) // Append the item to the array for this title
        })

        setDocumentList(groupedData)
    }

    useEffect(() => {
        getDocumentList()
        logEvent(
            'kyc_doc_upload_landed',
            'click',
            'kyc',
            localStorage.getItem('email'),
            userData?.merchantDetails?.m_id,
            userData?.merchantDetails?.short_name,
            userData?.userDetails?.name,
        )
    }, [])

    const handleDocSubmit = async () => {
        const response = await makeAPICall({
            method: 'post',
            url: process.env.REACT_APP_BASE_URL + APIEndPoints.updateMerchantOnboardingStatus + merchant_details.m_id,
            ...(userData?.merchantDetails?.onboarding_status?.kwik_checkout_app !== 'kyc_verification_failed' && {
                payload: {
                    current_onboarding_status: 'kyc_document_upload',
                },
            }),
        })

        if (response?.status === 200) {
            logEvent(
                'kyc_doc_upload_save_continue_clicked',
                'click',
                'kyc',
                localStorage.getItem('email'),
                userData?.merchantDetails?.m_id,
                userData?.merchantDetails?.short_name,
                userData?.userDetails?.name,
            )
            setCurrentStep(1)
        }
        dispatch(fetchUserDetails())
    }

    const showSampleDoc = (doc) => {
        setShowPreview(true)
        setPreviewDoc(doc)
    }

    const handlePreview = (name) => {
        getKycDoc(name, setVisible, setDocumentUrl)
    }

    const disableDocumentSubmitButton = () => {
        return (
            documentList?.some((doc) => doc.details?.upload_url && !doc.details?.uploaded) ||
            userData?.merchantDetails?.onboarding_status?.kwik_checkout_app === 'kyc_verification_pending' ||
            newStepConfiguration[userData?.merchantDetails?.onboarding_status?.kwik_checkout_app]?.id >= 20
        )
    }

    const deleteFile = async (name) => {
        await makeAPICall({
            method: 'post',
            url: process.env.REACT_APP_BASE_URL + APIEndPoints.deleteKycDocument,
            payload: {
                file_name: name,
            },
        }).then((res) => {
            getDocumentList()
        })
    }

    const handleDelete = async (item) => {
        setOcrResult(ocrResult?.filter((i) => i?.imageName !== item?.name))
        await deleteFile(item.name)
    }

    const handleImageUpload = async (image, imageName, setLoadingAI, setInputImageName, userData) => {
        if (!image) return

        setLoadingAI(true)
        setInputImageName(imageName)

        const response = await makeAPICall({
            url: process.env.REACT_APP_BASE_URL + APIEndPoints.openAIOCR,
            method: 'post',
            payload: { file: imageName },
            skipLoader: true,
        })

        if (response?.status === 200) {
            const aiResponse = response?.data?.data?.ai_response
            console.log('AI Response: ', aiResponse?.ai_response)
            setLoadingAI(false)
            setOcrApiError(false)
            return { aiResponse, imageName }
        } else {
            setOcrApiError(true)
            setLoadingAI(false)
            return { aiResponse: null, imageName: null }
        }
    }

    const handleImageChange = async (e, item) => {
        const entityType = Number(userData?.kycData?.entity_type || sessionStorage.getItem('entity_type'))
        const gstCertificate = userData?.kycData?.gst_certificate
        const isNameAllowed = !NON_OCR_DOCS?.includes(item.name)

        const isEntityType10Valid =
            entityType === 10 &&
            (item.name !== 'business_registration_proof' || gstCertificate === 'Yes') &&
            !(gstCertificate === 'No' && item.name === 'address_proof_front_page')

        const isEntityType9Valid = entityType === 9 && item.name !== 'business_registration_proof'

        const isOtherEntityType = entityType !== 9 && entityType !== 10

        if (isNameAllowed && (isEntityType10Valid || isEntityType9Valid || isOtherEntityType)) {
            const OcrResponse = await handleImageUpload(e, item?.name, setLoadingAI, setInputImageName, userData)

            setOcrResult((prev) => {
                const isDuplicate = prev.some(
                    (entry) =>
                        entry?.imageName === OcrResponse?.imageName &&
                        entry?.aiResponse?.doc_type === OcrResponse?.aiResponse?.doc_type,
                )
                return isDuplicate ? prev : [...prev, OcrResponse]
            })
        }
    }

    const updateMerchantOnboardingStatusToInitialStage = async () => {
        await makeAPICall({
            method: 'post',
            url:
                process.env.REACT_APP_BASE_URL +
                APIEndPoints.updateMerchantOnboardingStatus +
                userData?.merchantDetails?.m_id,
            payload: {
                current_onboarding_status: 'kyc_viewed',
            },
        })

        dispatch(fetchUserDetails())
    }

    const handleOk = () => {
        sessionStorage.setItem('entity_type', '')
        updateMerchantOnboardingStatusToInitialStage()
        setIsModalOpen(false)
    }

    const handleRevert = () => {
        setIsModalOpen(true)
    }

    const showChangeButton = () =>
        stepConfiguration[userData?.merchantDetails?.onboarding_status?.kwik_checkout_app]?.id >= 9 &&
        stepConfiguration[userData?.merchantDetails?.onboarding_status?.kwik_checkout_app]?.id < 13

    return (
        <div className='flex flex-col items-center ml-10'>
            <div className={`flex w-full items-center ${showChangeButton() ? 'justify-between' : 'justify-start'}`}>
                <p className='text-3xl font-bold'>Upload your documents for KYC</p>

                {showChangeButton() && (
                    <Button
                        onClick={handleRevert}
                        variant='primary'
                        className='border-red-500 text-red-500 shadow-none bg-white hover:!bg-white hover:!text-red-500'
                        icon={<ReloadOutlined />}
                    >
                        Change Business Type/Category
                    </Button>
                )}
            </div>
            <br />
            <Alert
                type='info'
                className='border-l-8 border-l-blue-500 p-0.5 pr-1 w-full'
                description={
                    <ol className='w-full'>
                        {UPLOAD_DOCS_GUIDELINES.map((entry, index) => (
                            <li key={index} className='text-[#004B8D] text-sm mb-2'>
                                {entry}
                            </li>
                        ))}
                    </ol>
                }
            />

            <div className='flex flex-col w-full mt-6 gap-2'>
                {documentList?.map((doc, index) => (
                    <div key={index}>
                        {Object.keys(doc?.details)?.includes('upload_url') && (
                            <DocsUploadCard
                                doc={doc}
                                onShowSampleClick={showSampleDoc}
                                openUploadModal={(doc) => {
                                    onUploadClick(doc)
                                }}
                                handlePreview={handlePreview}
                                uploadedFileName={uploadedFileName}
                                resolvedErrors={resolvedErrors}
                                setResolvedErrors={setResolvedErrors}
                                deleteFile={deleteFile}
                                hasGenericError={hasGenericError}
                            />
                        )}
                    </div>
                ))}
            </div>
            <div className='flex mt-5 w-full mb-5'>
                <Button
                    variant='primary'
                    className='w-full font-semibold h-10'
                    onClick={handleDocSubmit}
                    disabled={disableDocumentSubmitButton()}
                >
                    Submit & Continue
                </Button>
            </div>
            {showPreview && (
                <SampleDocumentPreview
                    isOpen={showPreview}
                    setIsOpen={setShowPreview}
                    previewDoc={previewDoc}
                    setPreviewDoc={setPreviewDoc}
                />
            )}
            {openUploadModal && (
                <DocsUploadModal
                    item={currentDoc}
                    visible={openUploadModal}
                    onClose={() => {
                        setOpenUploadModal(false)
                        setOcrResult([])
                        setShowLowScoreAlert(false)
                    }}
                    doc={currentDoc?.details}
                    getDocumentList={getDocumentList}
                    onShowSampleClick={showSampleDoc}
                    setIsUploading={setIsUploading}
                    setUploadedFileName={setUploadedFileName}
                    setOpenStatusModal={setOpenStatusModal}
                    setUploadProgress={setUploadProgress}
                    setOcrResult={setOcrResult}
                    ocrResult={ocrResult}
                    setLoadingAI={setLoadingAI}
                    setInputImageName={setInputImageName}
                    resolvedErrors={resolvedErrors}
                    setResolvedErrors={setResolvedErrors}
                    handleDelete={handleDelete}
                    showLowScoreAlert={showLowScoreAlert}
                    setOcrApiError={setOcrApiError}
                    handleImageChange={handleImageChange}
                    setUploadedFile={setUploadedFile}
                    ocrApiError={ocrApiError}
                    setShowLowScoreAlert={setShowLowScoreAlert}
                />
            )}
            {visible && (
                <DocumentPreviewModal
                    visible={visible}
                    onClose={() => setVisible(false)}
                    documentUrl={documentUrl}
                    documentName='document_name'
                />
            )}
            {openStatusModal && (
                <DocsStatusModal
                    item={currentDoc}
                    doc={currentDoc?.details}
                    ocrResult={ocrResult}
                    setOcrResult={setOcrResult}
                    visible={openStatusModal}
                    onClose={() => {
                        setOpenStatusModal(false)
                        setUploadProgress(0)
                    }}
                    uploadProgress={uploadProgress}
                    uploadedFileName={uploadedFileName}
                    onShowSampleClick={showSampleDoc}
                    loadingAI={loadingAI}
                    openUploadModal={() => {
                        setOpenUploadModal(true)
                    }}
                    handlePreview={handlePreview}
                    isUploading={isUploading}
                    setCurrentDoc={setCurrentDoc}
                    handleDelete={handleDelete}
                    showLowScoreAlert={showLowScoreAlert}
                    setShowLowScoreAlert={setShowLowScoreAlert}
                    ocrApiError={ocrApiError}
                    handleImageChange={handleImageChange}
                    uploadedFile={uploadedFile}
                />
            )}
            <Modal
                title='Do you want to proceed ?'
                open={isModalOpen}
                onOk={handleOk}
                onCancel={() => setIsModalOpen(false)}
            >
                Changing your business type or category will reset uploaded documents. Do you wish to continue?
            </Modal>
        </div>
    )
}

export default DocumentsUploads
